import * as React from "react";
import { $, merge, media } from "glamor";
import { ProductTile } from "components/product-collection/product-collection-views/product-collection-tile-view/product-tile";
import { SmallTile } from "components/product-collection/product-collection-views/product-collection-grid-view/tool-panels/shop-tool-panel/small-tile";
import { Grid } from "react-virtualized/dist/commonjs/Grid";
import { AutoSizer } from "react-virtualized/dist/commonjs/AutoSizer";
import { InfiniteLoader } from "react-virtualized/dist/commonjs/InfiniteLoader";
import Separator from "components/items/separator";
import Image from "components/items/image-separator";
import { Set, List, Map, fromJS } from "immutable";
import uuid from "uuid/v4";
import CircularProgress from "material-ui/CircularProgress";
import { DEFAULT_LINESHEET_FILTERS, Item as ProductDisplayFieldPaths } from "constants/product-collection";
import Filters from "components/filters/filters";
import { get, isEqual } from "lodash";
import { formatMessage } from "lib/globalize-util";
import Dialog from "components/dialog/dialog";
import Button from "components/form/button";
import { DATATEST } from "constants/integration";
import {
    getCollectionSwatches,
    getVariantColorIds,
    getVariantSwatches,
    IDisplayFields,
    ISchemaKey,
} from "helpers/product-collection";
import { LoadingShimmer } from "components/product-collection/product-collection-side-panel/loading-shimmer";
import { imageFallback } from "helpers/product-helpers";
import type { IScope } from "reducers/scope";
import { IProduct } from "records/item/item";
import { getProductShopViewFields, getProductShopViewFieldsFallback, IShopViewFields } from "helpers/collection";
import {
    getProductHeroImage,
    getProductImages,
} from "../product-collection-views/product-collection-detail-view/helpers";
import { IShopViewSettings } from "../manage-shop-view/types";

const MOBILE_PAGE_SIZE = 20;
const FILTERS_WIDTH = 240;
const tombstoneRows = (grid, { columns = 0, ProductCard, fetchingRows = 0, resultLength = 0 }) => {
    if (!fetchingRows || !grid) {
        return;
    }

    // fill out remaining row
    while (grid[grid.length - 1] && grid[grid.length - 1].length < columns) {
        grid[grid.length - 1].push({
            width: ProductCard.width,
            height: ProductCard.height,
            index: resultLength,
        });
        resultLength += 1;
    }

    for (let i = 0; i < fetchingRows; i++) {
        grid.push([]);
        for (let j = 0; j < columns; j++) {
            grid[grid.length - 1].push({
                width: ProductCard.width,
                height: ProductCard.height,
                index: resultLength,
            });
            resultLength += 1;
        }
    }
};

const calculateHeight = (type) => {
    let height;
    switch (type) {
        case "separator":
            height = 24;
            break;
        case "image":
            height = 330;
            break;
        case "item":
        default:
            height = 300;
            break;
    }

    return height;
};

const getGridForItems = ({ result = [], columns, entities, sidePanelWidth, ProductCard, fetchingRows }) => {
    const grid = [];
    const { items = {}, entries = {} } = entities;
    let forceNewLine = false;
    let height = 0;
    for (let i = 0; i < result.length; i++) {
        const id = result[i];
        const entry = entries[id];

        if (!entry) {
            continue;
        }
        const item = items[entry.item];

        const isImageOrSeparator = entry.type === "image" || entry.type === "separator";
        const spanAllColumns = entry && isImageOrSeparator ? true : false;
        const productSnapshot = fromJS(item?.product?.snapshot?.root?.mutable_map);
        let lastRow = grid.length - 1;

        if (grid.length === 0 || spanAllColumns || forceNewLine || grid[grid.length - 1].length >= columns) {
            grid.push([]);
            lastRow = grid.length - 1;
            forceNewLine = spanAllColumns;
            if (spanAllColumns) {
                height += calculateHeight(entry.type);
            } else {
                height += ProductCard.height + ProductCard.verticalOffset;
            }
        }

        if (spanAllColumns) {
            grid[lastRow].push({
                entry,
                item,
                productSnapshot,
                width: sidePanelWidth,
                height: calculateHeight(entry.type),
                index: i,
            });
        } else {
            grid[lastRow].push({
                entry,
                item,
                productSnapshot,
                width: ProductCard.width,
                height: ProductCard.height,
                index: i,
            });
        }
    }

    tombstoneRows(grid, {
        columns,
        fetchingRows,
        ProductCard,
        resultLength: result.length,
    });

    return { grid, height };
};

interface IProps {
    app: any;
    collectionEditable: boolean;
    entities: any;
    entries: any;
    inAssortmentProductIds?: Set<string>;
    inAssortmentProducts?: { [key: string]: string[] };
    isViewDefFetching: boolean;
    itemsId: string;
    items: List<any>;
    location: any;
    onAddItem: (itemsId, attributes?, index?) => void;
    onFetchEntries: (catalogId, cacheKey, filters, fromAssortment?) => void;
    onFetchEntriesNextPage: (catalogId, nextPage, fromAssortment?) => void;
    scope: any;
    sidePanelWidth: number;
    viewDef?: any;
    viewDefEntities: any;
    viewDefIds: string[];
    overrideStyles?: any;
    router: any;
    catalog?: any;
    filters?: Map<any, any>;
    additionalFilters?: any[];
    isSidePanel?: boolean;
    fromAssortment?: boolean;
    allowDuplicateProduct: boolean;
    shopViewFields: IDisplayFields;
    itemDisplayProperty;
    brandEntities: any;
    shopViewSettings?: IShopViewSettings;
    rawItemDisplayProperty?: ISchemaKey[];
}

export class ProductCollectionCatalog extends React.PureComponent<IProps, any> {
    _onRowsRendered: any;
    _grid: any;

    private muiStyles = {
        circularProgress: {
            width: "40px",
            margin: "100px auto",
        },
        imageSeparator: {
            paddingLeft: "8px",
            paddingRight: "8px",
            paddingTop: "8px",
        },
        datepickerIcon: {
            position: "relative",
            left: "-10px",
            top: "2px",
        },
        noResults: {
            display: "block",
            padding: "8px 0 0",
            position: "relative",
            height: "64px",
            lineHeight: "56px",
            textAlign: "center",
            fontWeight: "bold",
            fontSize: "18px",
        },
    };

    private styles = merge({
        minWidth: "240px",
    });

    constructor(props) {
        super(props);

        this.state = {
            grid: [],
            height: 0,
            filters: this.props.filters,
            isFilterDialogOpen: false,
            reloadFilters: false,
        };
    }

    computeGrid = (props, ProductCard) => {
        const {
            app,
            entries,
            entities,
            sidePanelWidth,
            overrideStyles = { gridWidth: undefined, viewHorizontalOffset: undefined, viewWidth: undefined },
            isSidePanel,
        } = props;

        const { gridWidth, viewHorizontalOffset, viewWidth } = overrideStyles;

        const filtersWidth = isSidePanel ? 0 : FILTERS_WIDTH;

        const newGridWidth = gridWidth !== undefined ? gridWidth : window.innerWidth - filtersWidth;

        const newViewWidth = viewWidth !== undefined ? viewWidth : ProductCard.width;
        const newViewHorizontalOffset =
            viewHorizontalOffset !== undefined ? viewHorizontalOffset : ProductCard.horizontalOffset;
        const maxWidth = ProductCard.width * 4;
        const columns = Math.floor(Math.min(newGridWidth, maxWidth) / (newViewWidth + newViewHorizontalOffset)) || 1;
        let grid = this.state.grid;
        let height = 0;

        const pageSize = app.touchDevice ? MOBILE_PAGE_SIZE : DEFAULT_LINESHEET_FILTERS.__limit;
        const fetchingRows = entries.isFetching ? Math.floor(pageSize / columns) : 0;

        const result = getGridForItems({
            result: entries.results,
            columns,
            entities,
            sidePanelWidth,
            ProductCard,
            fetchingRows,
        });
        height = result.height;
        grid = result.grid;

        this.setState({
            columns,
            grid,
            height,
            estimatedRows: grid ? grid.length : 0,
        });
    };

    UNSAFE_componentWillMount() {
        const ProductCard = this.props.isSidePanel ? SmallTile : ProductTile;
        this.computeGrid(this.props, ProductCard);

        this.setState({
            ProductCard,
        });

        this.setState({ filters: this.props.filters });
        window.addEventListener("resize", this.handleResize, false);
    }

    componentDidMount() {
        this.setFiltersAndFetchEntries();
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.handleResize, false);
    }

    handleResize = (event) => {
        this.computeGrid(this.props, this.state.ProductCard);
    };

    UNSAFE_componentWillReceiveProps(nextProps) {
        const hasBrandScope = nextProps.scope.brand && nextProps.scope.brand._id;
        const missingEntities = !nextProps.entities.items || !nextProps.entities.entries;
        const userNavigatedHere = hasBrandScope && nextProps.entities.schemas && missingEntities;
        if (
            (userNavigatedHere && !nextProps.entries.isFetching) ||
            (hasBrandScope && nextProps.scope.brand !== this.props.scope.brand)
        ) {
            if (window.location.href.indexOf("/brand/") === -1 && window.location.href.indexOf("/dashboard") === -1) {
                this.setFiltersAndFetchEntries(nextProps);
            }
        }
        if (
            this.props.entries &&
            nextProps.entries &&
            (this.props.sidePanelWidth !== nextProps.sidePanelWidth ||
                this.props.entries.isFetching !== nextProps.entries.isFetching ||
                get(this.props.entries, "results.length", 0) !== get(nextProps.entries, "results.length", 0) ||
                this.props.entities.entries !== nextProps.entities.entries)
        ) {
            this.computeGrid(nextProps, this.state.ProductCard);
        }
    }

    UNSAFE_componentWillUpdate(nextProps, nextState) {
        if (nextProps.filters !== this.props.filters) {
            this.setState({ filters: nextProps.filters });
        }

        if (!isEqual(this.state.filters, nextState.filters)) {
            this.setFiltersAndFetchEntries(nextProps, nextState);
        }
    }

    componentDidUpdate(prevProps) {
        if (
            this._grid &&
            (prevProps.entries.results !== this.props.entries.results ||
                prevProps.inAssortmentProducts !== this.props.inAssortmentProducts)
        ) {
            this._grid.recomputeGridSize(); // row heights could have changed depending on label and item order
        }
        if (this.props.catalog?.id !== prevProps.catalog?.id) {
            this.setState({ reloadFilters: true });
        }
        if (this.state.reloadFilters && this.props.catalog?.id === prevProps.catalog?.id) {
            this.setState({ reloadFilters: false });
        }
        if (this._grid && prevProps.shopViewSettings !== this.props.shopViewSettings) {
            this._grid.recomputeGridSize();
        }
        if (
            this._grid &&
            prevProps.entries.results !== this.props.entries.results &&
            prevProps.entries.cacheKey !== this.props.entries.cacheKey
        ) {
            this._grid.scrollToPosition({ scrollTop: 0 });
        }
    }

    cellRenderer = ({ columnIndex, rowIndex, key, style }) => {
        const {
            app,
            collectionEditable,
            scope,
            inAssortmentProducts,
            allowDuplicateProduct,
            brandEntities,
            shopViewSettings,
            rawItemDisplayProperty,
        } = this.props;
        const { grid, ProductCard } = this.state;

        // Loading, show tombstones
        if (!grid[rowIndex]) {
            return (
                <div style={style} key={key}>
                    <LoadingShimmer />
                </div>
            );
        }
        const node = grid[rowIndex][columnIndex];

        if (!node) {
            // dont render things to the right of the label
            return null;
        }

        if (!node.entry) {
            return (
                <div style={style} key={key}>
                    <LoadingShimmer />
                </div>
            );
        }

        style = {
            ...style,
            height: node.height,
            width: node.entry && (node.entry.type === "separator" || node.entry.type === "image") ? "100%" : node.width,
            carouselImage: {
                backgroundSize: "contain",
            },
        };

        if (node.entry && node.entry.type === "separator") {
            return (
                <Separator
                    key={key}
                    app={app}
                    style={style}
                    index={node.index}
                    width={node.width}
                    catalogEntry={node.entry}
                />
            );
        } else if (node.entry && node.entry.type === "image") {
            style = {
                ...style,
                ...this.muiStyles.imageSeparator,
            };
            return (
                <Image app={app} catalogEntry={node.entry} key={key} style={style} width={node.width} scope={scope} />
            );
        } else {
            const product = node.productSnapshot;
            const oldProduct = node.item;
            let productId, primaryImageUrl, secondaryImageUrl, variantColorIds, additionalColors;
            let productShopView: IShopViewFields;

            if (product) {
                // Get data from product snapshot
                productId = product.getIn(ProductDisplayFieldPaths.FIELDS.PRODUCT_ID);
                const brandId = product.getIn(ProductDisplayFieldPaths.FIELDS.BRAND_ID);
                const brand = brandEntities[brandId];
                primaryImageUrl = getProductHeroImage(brand, product, 400);
                secondaryImageUrl = getProductImages(brand, product, 400)?.[1];
                const variantColors = product.getIn(ProductDisplayFieldPaths.FIELDS.VARIANT_COLORS);
                variantColorIds = getVariantColorIds(variantColors);
                additionalColors = getCollectionSwatches(product);
                productShopView =
                    getProductShopViewFields(rawItemDisplayProperty, product, shopViewSettings) ||
                    getProductShopViewFieldsFallback();
            } else {
                productId = oldProduct._id;
                const oldProductImages = getOldProductImages(this.props.scope, oldProduct);
                primaryImageUrl = oldProductImages.primaryImageUrl;
                secondaryImageUrl = oldProductImages.secondaryImageUrl;
                variantColorIds = get(oldProduct, "__variants[0].items", []);
                additionalColors = getVariantSwatches(oldProduct);
                productShopView = {
                    brandName: scope.brand.name,
                    title: oldProduct.name,
                    subTitle: oldProduct.style_number,
                    color: oldProduct.color,
                    retailPrice: oldProduct.formattedMSRPRange("USD", scope),
                };
            }

            const handleItemClick = () => {
                this.handlePreviewItem(productId);
            };

            const handleAddItem = (event: Event) => {
                event.stopPropagation();
                this.handleAddItem(oldProduct);
            };

            const selected = !!inAssortmentProducts[productId];
            let onSelectItem = null;
            if (collectionEditable) {
                onSelectItem = handleAddItem;
            }

            const { items } = this.props.entities;
            const unsynced = !items[productId]?.product;

            return (
                <div data-test={DATATEST.ASSORTMENTS.SHOP_PRODUCT_CARD} style={style} key={node.index}>
                    <ProductCard
                        isSortable={false}
                        id={productId}
                        productId={productId}
                        imageUrl={primaryImageUrl}
                        secondaryImageUrl={secondaryImageUrl}
                        brandName={productShopView.brandName}
                        primaryAttribute={productShopView.title}
                        secondaryAttribute={productShopView.retailPrice}
                        tertiaryAttributes={[productShopView.subTitle, productShopView.color]}
                        costPrice={productShopView.costPrice}
                        additionalColors={additionalColors}
                        onSelectItem={onSelectItem}
                        selected={selected}
                        showLinking={false}
                        onLinkItem={this.handleLinkItem}
                        index={node.index}
                        onItemClick={handleItemClick}
                        hasAdditionalVariants={variantColorIds.length > 1 && additionalColors.length <= 1}
                        allowDuplicateProduct={allowDuplicateProduct}
                        unsynced={unsynced}
                        shopViewSettings={shopViewSettings}
                    />
                </div>
            );
        }
    };

    handleLinkItem = () => {
        // no-op
    };

    handleAddItem = (product) => {
        const { itemsId, items, onAddItem, router, inAssortmentProducts } = this.props;
        const productId = product._id;
        const productImages =
            product.__images &&
            product.__images.reduce((accumulator, image, index) => {
                accumulator[`product_image_${index + 1}`] = image;
                return accumulator;
            }, {});
        const selected = !!inAssortmentProducts[productId];
        return onAddItem({
            itemsId,
            attributes: {
                fields: {
                    id: { string_value: uuid() },
                    product_id: { string_value: productId },
                    ...productImages,
                },
            },
            index: items ? items.size : 0,
            productId,
            collectionId: router.params.id,
            selected,
        });
    };

    rowHeight = ({ index }) => {
        const { grid, ProductCard } = this.state;
        return (grid[index] ? grid[index][0].height : ProductCard.height) + ProductCard.verticalOffset;
    };

    handleFilterChange = (newFilters) => {
        const query = { ...this.props.location.query };
        delete query.brand; // get rid of weird brand=crap

        if (newFilters.size > 0) {
            query.filters = JSON.stringify(newFilters.toJS());
        } else {
            delete query.filters;
        }

        this.props.router.replace({
            pathname: this.props.location.pathname,
            query,
        });
    };

    removeExternalFilters = (filters: Map<any, any>) => {
        const { additionalFilters } = this.props;
        if (!additionalFilters) {
            return filters;
        }
        return additionalFilters.reduce((acc, curr) => acc.remove(curr.__key), filters);
    };

    setFiltersAndFetchEntries = (props = this.props, state = this.state) => {
        const { onFetchEntries, router, fromAssortment } = props;
        const { catalog } = router.params;
        const { filters } = state;
        const cacheKey = this.getEntriesCacheKey(props, state);
        onFetchEntries(
            catalog,
            cacheKey,
            this.generateEntriesQueryParams(this.removeExternalFilters(filters)),
            fromAssortment
        );
    };

    getEntriesCacheKey = (props = this.props, state = this.state) => {
        const { scope } = props;
        const { filters } = state;
        const filterString = JSON.stringify(filters.toJS());

        let key = `${scope.brand._id}::${filterString === "{}" ? "" : filterString}`;

        if (scope.viewAs) {
            key = `${key}::company:${props.scope.viewAs._id}`;
        }

        if (scope.viewAsGuest) {
            key = `${key}::company:guest`;
        }

        return key;
    };

    generateEntriesQueryParams = (filters) => {
        const query: any = {
            ...DEFAULT_LINESHEET_FILTERS,
        };

        let filtersHaveValues = false;
        if (filters) {
            filters.valueSeq().some((filterVal) => {
                if (filterVal) {
                    filtersHaveValues = true;
                    return true;
                }
            });
        }
        if (filtersHaveValues) {
            query.__type = "item"; // filter out separators only if there are legit filters
            filters = filters.toJS();
            if (filters.__active) {
                switch (filters.__active) {
                    case "active":
                        filters.__active = true;
                        break;
                    case "inactive":
                        filters.__view_disabled = true;
                        filters.__active = false;
                        break;
                    default:
                        filters.__view_disabled = true;
                        delete filters.__active;
                }
            }
            Object.assign(query, filters);
        }

        return query;
    };

    handleIsRowLoaded = ({ index }) => {
        const { entries, entities } = this.props;
        const entryId = entries.results[index];
        const entry = entryId && entities.entries[entryId];
        return !!entry;
    };

    handleLoadMoreRows = () => {
        const { entries, router, fromAssortment } = this.props;
        const { catalog } = router.params;
        if (entries.hasMoreResults && !entries.isFetching) {
            this.props.onFetchEntriesNextPage(catalog, entries.nextPage, fromAssortment);
        }
    };

    handleSectionRendered = ({ columnStartIndex, columnStopIndex, rowStartIndex, rowStopIndex }) => {
        const { columns } = this.state;
        const startIndex = rowStartIndex * columns + columnStartIndex;
        const stopIndex = rowStopIndex * columns + columnStopIndex;
        this._onRowsRendered({
            startIndex,
            stopIndex,
        });
    };

    handleSetGridRef = (ref) => (this._grid = ref);

    render() {
        const {
            overrideStyles = {
                gridWidth: undefined,
                viewHorizontalOffset: undefined,
                viewWidth: undefined,
                headerHeight: 0,
            },
            scope,
            isSidePanel,
            catalog,
            additionalFilters,
        } = this.props;
        const { columns, grid, estimatedRows, ProductCard, filters, isFilterDialogOpen, reloadFilters } = this.state;
        const { gridWidth, viewHorizontalOffset, viewWidth, headerHeight } = overrideStyles;
        const newViewWidth = viewWidth !== undefined ? viewWidth : ProductCard.width;
        const newViewHorizontalOffset =
            viewHorizontalOffset !== undefined ? viewHorizontalOffset : ProductCard.horizontalOffset;
        const shouldReloadFilters = !reloadFilters && catalog?.brand_id === scope?.brand?._id;

        if (!scope || !scope.brand) {
            return (
                <div style={this.muiStyles.circularProgress}>
                    <CircularProgress />
                </div>
            );
        }

        return (
            <div {...this.getStyles("100%")}>
                {/* Don't show Filters when this component is a ag-grid right panel */}
                {!isSidePanel && shouldReloadFilters && (
                    <div {...this.styles} style={{ maxHeight: "100%", overflow: "hidden", overflowY: "scroll" }}>
                        <Filters
                            resource={"item"}
                            clearAll={true}
                            activeFiltersValues={filters}
                            onFilterChange={this.handleFilterChange}
                            hiddenFilters={["__inventory"]}
                            title={formatMessage("Filters")}
                            containerClassName="filters"
                            catalog={catalog}
                            filterInclusions={additionalFilters}
                            omitInclusions={true}
                        />
                        <Button
                            className="filterButton"
                            onClick={this.handleOpenFilterDialog}
                            label={formatMessage("Filters")}
                        />
                        <Dialog open={isFilterDialogOpen} onRequestClose={this.handleCloseFilterDialog}>
                            <Filters
                                resource={"item"}
                                clearAll={false}
                                activeFiltersValues={filters}
                                onFilterChange={this.handleFilterChange}
                                hiddenFilters={["__inventory"]}
                                title={formatMessage("Filters")}
                                catalog={catalog}
                                filterInclusions={additionalFilters}
                                omitInclusions={true}
                            />
                        </Dialog>
                    </div>
                )}
                <div className="entriesContainer">
                    <div ref={this.bindRef} className="results">
                        <InfiniteLoader
                            isRowLoaded={this.handleIsRowLoaded}
                            loadMoreRows={this.handleLoadMoreRows as any}
                            rowCount={10000}
                            threshold={1}
                        >
                            {({ onRowsRendered }) => {
                                this._onRowsRendered = onRowsRendered;
                                return (
                                    <AutoSizer>
                                        {({ height, width }) => {
                                            const newGridWidth = gridWidth !== undefined ? gridWidth : width;
                                            if (!height) {
                                                return null;
                                            }
                                            return (
                                                <Grid
                                                    cellRenderer={this.cellRenderer}
                                                    columnCount={columns}
                                                    columnWidth={
                                                        estimatedRows
                                                            ? newViewWidth + newViewHorizontalOffset
                                                            : newGridWidth
                                                    }
                                                    height={height - headerHeight}
                                                    rowCount={estimatedRows}
                                                    rowHeight={this.rowHeight}
                                                    noContentRenderer={this.noContentRenderer}
                                                    width={width}
                                                    ref={this.handleSetGridRef}
                                                    onSectionRendered={this.handleSectionRendered}
                                                    overscanRowCount={1}
                                                />
                                            );
                                        }}
                                    </AutoSizer>
                                );
                            }}
                        </InfiniteLoader>
                    </div>
                </div>
            </div>
        );
    }

    private handleOpenFilterDialog = () => {
        this.setState({ isFilterDialogOpen: true });
    };

    private handleCloseFilterDialog = () => {
        this.setState({ isFilterDialogOpen: false });
    };

    private noContentRenderer = () => {
        return <div style={this.muiStyles.noResults as any}>No results found</div>;
    };

    private handlePreviewItem = (productId) => {
        const { router } = this.props;
        router.replace({
            pathname: router.location.pathname,
            query: {
                ...router.location.query,
                productId,
            },
        });
    };

    private bindRef = (ref) => {
        this.setState({
            scrollDiv: ref,
        });
    };

    private getStyles = (height) => {
        return merge(
            {
                padding: "0px",
                flex: "1",
                display: "flex",
                height: `calc( ${height} - 10px)`,
            },
            $(" > header", {
                marginLeft: "0px",
            }),
            $(" .entriesContainer", {
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
            }),
            $(" .ReactVirtualized__Grid__innerScrollContainer", {
                margin: "0 auto",
            }),
            $(" .results", {
                flexShrink: 1,
                flexGrow: 1,
            }),
            $(" .separatorLabel", {
                fontSize: "18px !important",
                background: "transparent !important",
                borderBottom: "none !important",
                paddingLeft: "8px !important",
                lineHeight: "40px !important",
            }),
            $(" .deliveryDates", {
                fontSize: "24px",
                fontWeight: "bold",
                width: "50%",
                margin: "0 auto",
                textAlign: "center",
            }),
            $(" .actionsContainer", {
                marginRight: "16px",
            }),
            $(" .filterButton", {
                display: "none !important",
            }),
            $(" .filters", {
                display: "block !important",
            }),
            media(
                "(max-width: 540px)",
                {
                    display: "block",
                },
                $(" > header", {
                    marginLeft: "0px",
                }),
                $(" .entriesContainer", {
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                }),
                $(" .ReactVirtualized__Grid__innerScrollContainer", {
                    margin: "0 auto",
                }),
                $(" .results", {
                    flexShrink: 1,
                    flexGrow: 1,
                }),
                $(" .separatorLabel", {
                    background: "transparent !important",
                    borderBottom: "none !important",
                    paddingLeft: "2px !important",
                    lineHeight: "40px !important",
                }),
                $(" .deliveryDates", {
                    fontSize: "24px",
                    fontWeight: "bold",
                    width: "50%",
                    margin: "0 auto",
                    textAlign: "center",
                }),
                $(" .actionsContainer", {
                    marginRight: "16px",
                }),
                $(" .filterButton", {
                    display: "block !important",
                    margin: "10px auto !important",
                }),
                $(" .filters", {
                    display: "none !important",
                })
            )
        );
    };
}

interface ProductImages {
    primaryImageUrl: string;
    secondaryImageUrl?: string;
}

function getOldProductImages(scope: IScope, product: IProduct): ProductImages {
    if (!product) {
        return {
            primaryImageUrl: imageFallback,
        };
    }

    if (scope.brand.getPreference("unlimited_images")) {
        return {
            primaryImageUrl: product.getImageUrl(scope, "M", 0),
            secondaryImageUrl: product.getImageUrl(scope, "M", 1),
        };
    }

    if (product.__images_cache?.length) {
        return {
            primaryImageUrl: product.__images_cache[0],
            secondaryImageUrl: product.__images_cache[1],
        };
    }

    return {
        primaryImageUrl: product.getImageUrl(scope, "T", 0),
    };
}
